/**
 * NominaClassifyDialog — Gestión Documental Fase C (Capa 1, mb45.com, 2026-10)
 * Clasifica un adjunto de email de la asesoría laboral conocida (mb45.com):
 * la IA propone tipo/empleado/periodo/importe/fecha con fuzzy-match de
 * empleados, el admin revisa/elige/edita y confirma el archivado en Drive.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, FileText, ExternalLink, FolderArchive, UserCheck, AlertTriangle, BrainCircuit } from 'lucide-react';
import { API_URL } from '../config';

const API = API_URL;

const TIPOS = [
  { value: 'nomina', label: 'Nómina' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'alta_ss', label: 'Alta SS' },
  { value: 'baja_ss', label: 'Baja SS / Extinción' },
  { value: 'otro', label: 'Otro' },
];

export const NominaClassifyDialog = ({ open, onOpenChange, token, emailId, attachment }) => {
  const [step, setStep] = useState('loading'); // loading | review | done
  const [form, setForm] = useState({ tipo_documento: 'nomina', empleado_nombre: '', empleado_user_id: null, periodo: '', importe: '', fecha_documento: '' });
  const [candidatos, setCandidatos] = useState([]);
  const [detectedName, setDetectedName] = useState('');
  const [aliasPrompted, setAliasPrompted] = useState(false);
  const [savingAlias, setSavingAlias] = useState(false);
  const [textPreview, setTextPreview] = useState('');
  const [remitente, setRemitente] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [resultDoc, setResultDoc] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open || !attachment) return;
    setStep('loading');
    setResultDoc(null);
    setAliasPrompted(false);
    (async () => {
      try {
        const res = await axios.post(
          `${API}/documentos-archivados/classify-nomina-attachment`,
          { email_id: emailId, attachment_filename: attachment.filename },
          { headers }
        );
        const d = res.data;
        const mejorCandidato = (d.empleado_candidatos || [])[0];
        // FIX (bug reportado): si hay un candidato de alta confianza (alias ya
        // aprendido o heurística de apodo, score >= 0.75), usar SU nombre
        // resuelto como valor del campo. Antes siempre se prefería el nombre
        // legal detectado por la IA, por lo que un alias ya enseñado nunca se
        // aplicaba automáticamente y el sistema volvía a preguntar cada vez.
        const nombreResuelto = (mejorCandidato && mejorCandidato.score >= 0.75)
          ? mejorCandidato.nombre
          : (d.empleado_nombre_detectado || mejorCandidato?.nombre || '');
        setForm({
          tipo_documento: d.tipo_documento || 'nomina',
          empleado_nombre: nombreResuelto,
          empleado_user_id: mejorCandidato?.score >= 0.75 ? mejorCandidato.user_id : null,
          periodo: d.periodo || '',
          importe: d.importe ?? '',
          fecha_documento: d.fecha_documento || '',
        });
        setCandidatos(d.empleado_candidatos || []);
        setDetectedName(d.empleado_nombre_detectado || '');
        setTextPreview(d.text_preview || '');
        setRemitente(d.remitente || '');
        setStep('review');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No se pudo analizar la nómina');
        onOpenChange(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attachment, emailId]);

  const selectCandidato = (c) => {
    setForm({ ...form, empleado_nombre: c.nombre, empleado_user_id: c.user_id });
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(
        `${API}/documentos-archivados/confirm-classify-nomina`,
        {
          email_id: emailId,
          attachment_filename: attachment.filename,
          tipo_documento: form.tipo_documento,
          empleado_nombre: form.empleado_nombre || null,
          empleado_user_id: form.empleado_user_id || null,
          periodo: form.periodo || null,
          importe: form.importe === '' ? null : parseFloat(form.importe),
          fecha_documento: form.fecha_documento || null,
        },
        { headers }
      );
      setResultDoc(res.data.documento);
      setStep('done');
      toast.success('Nómina archivada en Google Drive');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo archivar la nómina');
    } finally {
      setConfirming(false);
    }
  };

  const normalizar = (s) => (s || '').toLowerCase().trim();
  // No volver a preguntar si el nombre final ya coincide con un candidato de
  // alta confianza (score >= 0.97 = alias ya aprendido anteriormente) —
  // evita preguntar "¿recordar?" una y otra vez por algo que ya se enseñó.
  const yaEsAliasConocido = candidatos[0] && candidatos[0].score >= 0.97 &&
    normalizar(candidatos[0].nombre) === normalizar(form.empleado_nombre);
  const mostrarPromptAlias =
    step === 'done' && !aliasPrompted && detectedName && form.empleado_nombre &&
    normalizar(detectedName) !== normalizar(form.empleado_nombre) && !yaEsAliasConocido;

  const handleSaveAlias = async (recordar) => {
    setAliasPrompted(true);
    if (!recordar) return;
    setSavingAlias(true);
    try {
      await axios.post(
        `${API}/documentos-archivados/empleado-alias`,
        { alias: form.empleado_nombre, nombre_legal: detectedName, user_id: form.empleado_user_id || null },
        { headers }
      );
      toast.success(`Recordado: "${detectedName}" = "${form.empleado_nombre}"`);
    } catch (e) {
      toast.error('No se pudo guardar el alias');
    } finally {
      setSavingAlias(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white max-h-[88vh] flex flex-col" data-testid="nomina-classify-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FolderArchive className="h-5 w-5 text-amber-400" />
            Clasificar nómina (mb45)
          </DialogTitle>
          <DialogDescription className="text-slate-400 truncate">{attachment?.filename}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400" data-testid="nomina-classify-loading">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
              <p className="text-sm">Analizando nómina con IA...</p>
            </div>
          )}

          {step === 'review' && (
            <>
              <p className="text-xs text-slate-500">Remitente: {remitente || 'desconocido'}</p>
              <div className="space-y-2">
                <Label className="text-slate-300">Tipo de documento</Label>
                <Select value={form.tipo_documento} onValueChange={(v) => setForm({ ...form, tipo_documento: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="nomina-classify-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white">
                    {TIPOS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {candidatos.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Empleado sugerido (fuzzy-match)</Label>
                  <div className="flex flex-wrap gap-2">
                    {candidatos.map((c, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectCandidato(c)}
                        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                          form.empleado_nombre === c.nombre
                            ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                            : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-500'
                        }`}
                        data-testid={`nomina-candidato-${i}`}
                      >
                        <UserCheck className="h-3 w-3" />
                        {c.nombre}
                        <span className="opacity-60">{Math.round(c.score * 100)}%</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">Nombre del empleado</Label>
                {(candidatos.length === 0 || (candidatos[0]?.score ?? 0) < 0.4) && (
                  <div
                    className="flex items-center gap-1.5 text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-md px-2.5 py-1.5"
                    data-testid="nomina-no-match-warning"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    Ningún empleado conocido con alta similitud — revisa y escribe el nombre manualmente
                  </div>
                )}
                <Input
                  value={form.empleado_nombre}
                  onChange={(e) => setForm({ ...form, empleado_nombre: e.target.value, empleado_user_id: null })}
                  placeholder="Nombre y apellidos"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="nomina-classify-empleado-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-300">Periodo</Label>
                  <Input
                    type="month"
                    value={form.periodo}
                    onChange={(e) => setForm({ ...form, periodo: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="nomina-classify-periodo-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Importe (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.importe}
                    onChange={(e) => setForm({ ...form, importe: e.target.value })}
                    placeholder="0.00"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="nomina-classify-importe-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Fecha</Label>
                  <Input
                    type="date"
                    value={form.fecha_documento}
                    onChange={(e) => setForm({ ...form, fecha_documento: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="nomina-classify-fecha-input"
                  />
                </div>
              </div>
              {textPreview && (
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><FileText className="h-3 w-3" /> Texto extraído (vista previa)</p>
                  <p className="text-xs text-slate-400 whitespace-pre-wrap line-clamp-6">{textPreview}</p>
                </div>
              )}
            </>
          )}

          {step === 'done' && resultDoc && (
            <div className="space-y-3 py-4" data-testid="nomina-classify-done">
              <div className="flex items-center gap-2 text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm">
                <FolderArchive className="h-4 w-4 shrink-0" />
                Archivado en: {resultDoc.drive_folder_path}
              </div>
              {resultDoc.drive_web_view_link && (
                <a
                  href={resultDoc.drive_web_view_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-cyan-400 text-sm hover:underline"
                  data-testid="nomina-classify-drive-link"
                >
                  Ver en Google Drive <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {mostrarPromptAlias && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 space-y-2" data-testid="nomina-alias-prompt">
                  <p className="text-sm text-cyan-200 flex items-start gap-1.5">
                    <BrainCircuit className="h-4 w-4 shrink-0 mt-0.5" />
                    ¿Recordar que "{detectedName}" = "{form.empleado_nombre}" para que la próxima vez coincida automáticamente?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={savingAlias}
                      onClick={() => handleSaveAlias(true)}
                      className="bg-cyan-600 hover:bg-cyan-500"
                      data-testid="nomina-alias-remember-btn"
                    >
                      {savingAlias ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                      Sí, recordar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSaveAlias(false)}
                      className="border-slate-600 text-slate-300"
                      data-testid="nomina-alias-skip-btn"
                    >
                      No
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          {step === 'review' && (
            <Button
              onClick={handleConfirm}
              disabled={confirming}
              className="bg-amber-600 hover:bg-amber-500"
              data-testid="nomina-classify-confirm-btn"
            >
              {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />}
              Confirmar y archivar en Drive
            </Button>
          )}
          {step === 'done' && (
            <Button onClick={() => onOpenChange(false)} variant="outline" className="border-slate-600 text-slate-200" data-testid="nomina-classify-close-btn">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NominaClassifyDialog;
